import React, { useState, useEffect } from 'react';

interface ConfirmDestructiveProps {
    open: boolean;
    /** e.g. "Disable account?" */
    title: string;
    /** The exact thing being acted on — a single account, or a count for bulk actions. */
    entityLabel: string;
    /** Plain-language description of what happens. Never silent — always explains the effect. */
    consequence: React.ReactNode;
    /** e.g. "Disable", "Force cleanup" */
    confirmLabel: string;
    /**
     * When set, the confirm button stays disabled until the user types this exact
     * string. Use the account name for single-item actions; a fixed word (e.g.
     * "DISABLE") for bulk actions where typing every name isn't practical —
     * docs/design/01-independent-gui-base.md Pattern F: "typed/checked confirmation
     * for batch-destructive."
     */
    requireTypedConfirmation?: string;
    loading?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}

/**
 * Shared Pattern F confirm (docs/design/01-independent-gui-base.md §4): danger-styled
 * trigger already happened in the caller — this names the exact entity and consequence,
 * and never allows a silent destructive action. Net-new component, no legacy counterpart,
 * so it takes its real name directly (.cursor/rules/naming-parallel-implementations.mdc).
 */
const ConfirmDestructive: React.FC<ConfirmDestructiveProps> = ({
    open, title, entityLabel, consequence, confirmLabel,
    requireTypedConfirmation, loading, onConfirm, onCancel,
}) => {
    const [typed, setTyped] = useState('');

    useEffect(() => {
        if (open) setTyped('');
    }, [open]);

    if (!open) return null;

    const typedOk = !requireTypedConfirmation || typed === requireTypedConfirmation;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 font-plex"
            onClick={onCancel}
        >
            <div
                className="bg-surface rounded-lg shadow-lg w-full max-w-md border-t-4 border-danger mx-4"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-5">
                    <h3 className="text-base font-semibold text-danger">{title}</h3>
                    <div className="mt-2 inline-block font-plexMono text-sm bg-danger/10 text-danger px-2 py-1 rounded">
                        {entityLabel}
                    </div>
                    <div className="text-sm text-ink-secondary mt-3">{consequence}</div>
                    {requireTypedConfirmation && (
                        <div className="mt-4">
                            <label className="block text-xs text-ink-secondary mb-1">
                                Type <span className="font-plexMono font-semibold text-ink-primary">{requireTypedConfirmation}</span> to confirm
                            </label>
                            <input
                                autoFocus
                                className="w-full border border-border-strong rounded px-3 py-2 text-sm bg-surface text-ink-primary focus:outline-none focus:ring-2 focus:ring-danger/40 focus:border-danger"
                                value={typed}
                                onChange={(e) => setTyped(e.target.value)}
                            />
                        </div>
                    )}
                </div>
                <div className="flex justify-end gap-2 px-5 py-3 border-t border-border-subtle bg-surface-raised rounded-b-lg">
                    <button
                        onClick={onCancel}
                        className="px-4 py-2 text-sm text-ink-secondary hover:text-ink-primary transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={!typedOk || loading}
                        className={`px-4 py-2 text-sm font-medium rounded transition-colors ${
                            typedOk && !loading
                                ? 'bg-danger text-white hover:opacity-90'
                                : 'bg-surface-raised text-ink-secondary cursor-not-allowed border border-border-subtle'
                        }`}
                    >
                        {loading ? 'Working…' : confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConfirmDestructive;
